import { useMemo } from 'react';
import { Stack } from '@mui/material';

import {
  EditIcon,
  IPaginationProps,
  Page,
  Button,
  useAppLayoutContextConsumer,
} from '@occc-app/design-system';
import { LoadedData } from '@occc-app/routes/Wallet/types';
import { SegmentFragment, SegmentUploadedFileFragment } from '@occc/graphql';

import { SegmentDetailsCard } from './components/segment-details-card.component';
import { SegmentCsvFilesCard } from './components/segment-csv-files-card.component';
import { SegmentUploadFileCard } from './components/segment-upload-file-card.component';
import { SegmentAssignMembershipsCard } from './components/segment-assign-memberships-card.component';

import { SegmentUploadFileController } from '../../components/segment-upload-file.controller';
import { getBreadcrumbLinks } from '../../segments.utils';
import { SegmentAssignMembershipsController } from '../segment-assign-memberships/segment-assign-memberships.controller';

type SegmentDetailsPageProps = {
  segmentUuid: string;
  segmentData: LoadedData<SegmentFragment>;
  uploadedFilesData: LoadedData<SegmentUploadedFileFragment[]>;
  uploadedFilesPaginationProps: IPaginationProps;
  isEditAllowed: boolean;
  onEditClick: () => void;
  onAssignMembershipsLoadingStateChange?: (loading: boolean) => void;
  onMembershipsChanged?: (baselineMembershipCount: number) => void;
  isAddingOrRemovingInProgress?: boolean;
  hasIncompleteFiles?: boolean;
  isMembershipCountUpdating?: boolean;
};

export const SegmentDetailsPage = ({
  segmentUuid,
  segmentData,
  uploadedFilesData,
  uploadedFilesPaginationProps,
  isEditAllowed,
  onAssignMembershipsLoadingStateChange,
  onMembershipsChanged,
  isAddingOrRemovingInProgress = false,
  onEditClick,
  hasIncompleteFiles = false,
  isMembershipCountUpdating = false,
}: SegmentDetailsPageProps) => {
  const segmentName = segmentData.data?.name;

  const links = useMemo(() => getBreadcrumbLinks(segmentName), [segmentName]);
  useAppLayoutContextConsumer(links);

  const actions = isEditAllowed ? (
    <Button
      key='edit'
      color='secondary'
      variant='contained'
      onClick={onEditClick}
      startIcon={<EditIcon />}
      role='button'
    >
      Edit Details
    </Button>
  ) : null;

  const getPageTitle = () => {
    if (segmentName) {
      return segmentName;
    }

    if (segmentData.loading) {
      return 'Loading...';
    }

    return 'Segment Details';
  };

  const pageTitle = getPageTitle();

  const loading = segmentData.loading || isAddingOrRemovingInProgress;

  const hasExistingFiles = (uploadedFilesData.data?.length ?? 0) > 0;

  return (
    <Page
      browserTabTitle={pageTitle}
      actions={actions}
      pageHeaderProps={{
        title: pageTitle,
      }}
      containerProps={{ variant: 'md' }}
    >
      <Stack spacing={1}>
        <SegmentDetailsCard
          segmentData={segmentData}
          isMembershipCountUpdating={isMembershipCountUpdating}
        />

        {isEditAllowed && (
          <SegmentAssignMembershipsCard loading={loading}>
            <SegmentAssignMembershipsController
              uuid={segmentUuid}
              membershipCount={segmentData.data?.membershipCount ?? 0}
              onLoadingStateChange={onAssignMembershipsLoadingStateChange}
              onMembershipsChanged={onMembershipsChanged}
              shouldDisableControls={segmentData.loading || !segmentData.data}
            />
          </SegmentAssignMembershipsCard>
        )}

        {isEditAllowed && (
          <SegmentUploadFileCard loading={loading}>
            <SegmentUploadFileController
              segmentUuid={segmentUuid}
              shouldDisableControls={loading || hasIncompleteFiles}
              hasExistingFiles={hasExistingFiles}
              hasIncompleteFiles={hasIncompleteFiles}
            />
          </SegmentUploadFileCard>
        )}

        <SegmentCsvFilesCard
          filesData={uploadedFilesData}
          paginationProps={uploadedFilesPaginationProps}
        />
      </Stack>
    </Page>
  );
};
